import { Head, Link, router } from "@inertiajs/react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import FirstSyncCard from "@/components/brand/settings/products/first-sync-card"
import ProductsCardGrid from "@/components/brand/settings/products/products-card-grid"
import ProductsPager from "@/components/brand/settings/products/products-pager"
import ProductsTable from "@/components/brand/settings/products/products-table"
import SyncAnnouncer from "@/components/brand/settings/products/sync-announcer"
import SyncPanel from "@/components/brand/settings/products/sync-panel"
import { EmptyState } from "@/components/empty-state"
import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { useBrandSyncLive } from "@/hooks/use-brand-sync-live"
import AppLayout from "@/layouts/app-layout"
import { formatDateTime } from "@/lib/format-datetime"
import { cn } from "@/lib/utils"
import {
  brandSettingsIntegrationsPath,
  brandSettingsProductsPath,
  newBrandSettingsProductPath,
} from "@/routes"
import type { BrandSettingsProductsProps } from "@/types/products"

/**
 * OKS-931 — Brand Settings → Produits (UX Spec, binding).
 *
 * ## The five empty states are five DIFFERENT screens (PRD D8)
 *
 * E2 ("never synced"), E3 ("synced, found nothing") and E4 ("all archived")
 * are distinct STRINGS, not the same string with a hidden button. That
 * distinction is the entire reason the sync counters ship nullable with no
 * default — collapsing them in the UI would throw away at the last step the
 * thing the schema was shaped to preserve. E3 in particular is a real signal
 * the brand can act on (their products are all unpublished).
 *
 * ## Loading — two different things, deliberately not conflated (UX §3.2)
 *
 * A filter switch or a page turn is an ordinary Inertia visit: the current
 * rows stay mounted and NO skeleton swaps in. A settings list that blanks on
 * every page turn reads as broken.
 *
 * A running import is still not a page-loading state — the job is in the
 * background, the page is fully interactive, and the rows on screen are the
 * previous import's and still true.
 *
 * ⚠️ OKS-960 REPLACES the "no auto-refresh" half of this note rather than
 * annotating it. Updates DO now arrive without a reload — but there is still
 * **no polling, no auto-refresh and no `setInterval`**. The mechanism is a
 * push: `BrandChannel` sends a signal carrying prop NAMES and no data, and
 * this page answers with one targeted partial reload. Nothing runs on a
 * timer, and the look-again control fires only on click.
 *
 * ## Liveness follows the ACTOR, so this page has two renderings (AC19)
 *
 * You watch YOUR import. One a colleague started, the weekly one, or any
 * import while the socket is unavailable, renders the snapshot variant:
 * correct numbers as of page load, said plainly, with no spinner and nothing
 * animating — because the moving parts are what claim data is arriving.
 */
export default function BrandSettingsProducts({
  store,
  products,
  pagination,
  per_page,
  archived_view,
  has_archived,
  has_any_products,
  resync_available_in_seconds,
  can_manage,
  errors,
  first_sync_celebration,
  can_create_mission,
}: BrandSettingsProductsProps) {
  const { t, i18n } = useTranslation()

  // ⚠️ AC8 — rows land ONLY in the default view. A partial reload re-runs the
  // controller with the CURRENT query string, so reloading `products` on page
  // 3 would replace the rows the brand navigated to deliberately.
  const defaultView = !archived_view && pagination.page === 1
  const { connected } = useBrandSyncLive({
    only: defaultView
      ? [
          "store",
          "products",
          "pagination",
          "has_archived",
          "has_any_products",
          "resync_available_in_seconds",
          "first_sync_celebration",
        ]
      : ["store", "resync_available_in_seconds", "first_sync_celebration"],
  })

  // The look-again control (AC19): a one-shot reload on click, not polling and
  // not a timer, so the numbers visibly move and the control earns itself.
  const [refreshing, setRefreshing] = useState(false)
  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    router.reload({
      only: ["store", "resync_available_in_seconds", "first_sync_celebration"],
      preserveUrl: true,
      onFinish: () => setRefreshing(false),
    })
  }, [])

  // Live = signals are actually arriving for THIS viewer.
  const live = connected && store?.sync_progress?.started_by_viewer === true

  const syncedAt = formatDateTime(store?.last_synced_at ?? null, i18n.language)
  const hasProducts = products.length > 0
  // ⚠️ E4 keys off `has_archived`, a server-side EXISTS — NOT off
  // `last_sync_product_count === 0`. D11 makes that counter count
  // NON-ARCHIVED rows, so it reads 0 in E3 and E4 alike and cannot separate
  // them. See the prop's doc comment.
  const allArchived = !archived_view && !hasProducts && has_archived

  return (
    <AppLayout>
      <Head title={t("settings.brand.products.title")} />
      {/*
        ⚠️ `wide` — PO's ruling, UX Spec §1 amended to match.

        Every other Brand Settings page uses the narrow `max-w-xl` (576px)
        form column because every other one IS a form. Produits is a data
        table + pagination; its only `<form>` is a bare submit button. That is
        the exact case `wide` was added for in OKS-672 ("the relocated Team
        page is a wide data table + pagination that must not be squeezed") —
        see `Brand/Team/Index.tsx:142`.

        ⚠️ It is NOT the overflow fix, and must not be recorded as one. Under
        `table-layout: auto` the table sized to its CONTENT, so a wider
        container would have reduced the overflow, not removed it, and the
        defect would have returned with a longer title. The bound is
        `table-fixed` in `products-table.tsx`. `wide` is correct on its own
        terms — a table squeezed into a form column — and the two changes are
        independent.

        Root cause of the original narrow column, per PO: the page was
        specified as "structured exactly like Brand/Settings/Invites.tsx", a
        form page, and the column was inherited from that one sentence.
      */}
      <BrandSettingsLayout wide>
        <div data-testid="brand-products-settings" className="space-y-6">
          {/*
            OKS-932 — the create action sits beside the heading and is
            DOM-ABSENT when `can_manage` is false (never disabled, no
            explanatory sentence) — the same posture as OKS-931's sync action
            row. Rendered whether or not a store exists: it is not a sync
            control, so OKS-931 E1's "no sync control anywhere" still holds.
          */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <HeadingSmall
              title={t("settings.brand.products.title")}
              description={t("settings.brand.products.subtitle")}
            />
            {can_manage ? (
              <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
                <Link
                  href={newBrandSettingsProductPath()}
                  data-testid="brand-products-add"
                >
                  {t("settings.brand.products.add")}
                </Link>
              </Button>
            ) : null}
          </div>

          {store === null && !has_any_products ? (
            /*
              E1 — no Shopify connection at all. No SyncPanel, no filter, no
              pager, no table, and NO sync control anywhere on the page. The
              `?archived=1` param is inert because the filter is not rendered.
            */
            <EmptyState
              titleAs="h3"
              data-testid="brand-products-empty-not-connected"
              title={t("settings.brand.products.empty.not_connected.title")}
              description={t(
                "settings.brand.products.empty.not_connected.description",
              )}
              actions={
                <>
                  {can_manage ? (
                    <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
                      <Link href={newBrandSettingsProductPath()}>
                        {t("settings.brand.products.empty.not_connected.cta")}
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 w-full sm:h-9 sm:w-auto"
                  >
                    <Link href={brandSettingsIntegrationsPath()}>
                      {t(
                        "settings.brand.products.empty.not_connected.cta_secondary",
                      )}
                    </Link>
                  </Button>
                </>
              }
            />
          ) : (
            <>
              {/* The payoff sits ABOVE the panel — it answers "did my catalogue
                  arrive?", which the panel only implies. */}
              {first_sync_celebration !== null ? (
                <FirstSyncCard
                  celebration={first_sync_celebration}
                  canCreateMission={can_create_mission}
                />
              ) : null}

              {/* SyncPanel only when a store exists — unchanged (UX §2.1.6). */}
              {store !== null ? (
                <>
                  <SyncAnnouncer
                    progress={store.sync_progress}
                    live={live}
                    lastProductCount={store.last_sync_product_count}
                    lastVariantCount={store.last_sync_variant_count}
                  />
                  <SyncPanel
                    store={store}
                    resyncAvailableInSeconds={resync_available_in_seconds}
                    canManage={can_manage}
                    hasProducts={hasProducts}
                    live={live}
                    onRefresh={handleRefresh}
                    refreshing={refreshing}
                  />
                </>
              ) : null}

              {/*
                OKS-932 UX §2.1.7 — the filter renders whenever there is
                anything to filter. Gating it on a store stranded a store-less
                brand's archived manual products with no way to reach them.
              */}
              {store !== null || has_any_products ? (
                <FilterTabs archivedView={archived_view} />
              ) : null}

              {hasProducts ? (
                <>
                  {/*
                    ⚠️ No currency prop: each row carries its own, resolved
                    server-side. A manual product keeps the currency it was
                    entered in; `store.currency` would be unreachable here
                    anyway when `store === null`, which is the case this
                    ticket exists for.
                  */}
                  {/*
                    OKS-935 — `canManage` gates the TTL editors' AFFORDANCE
                    only; `ProductPolicy#manage?` is the guard and refuses with
                    a 404. `errors` carries a failed TTL PATCH back to the row
                    that produced it.
                  */}
                  <ProductsTable
                    products={products}
                    canManage={can_manage}
                    errors={errors}
                  />
                  <ProductsCardGrid
                    products={products}
                    canManage={can_manage}
                    errors={errors}
                  />
                  <ProductsPager
                    pagination={pagination}
                    perPage={per_page}
                    archivedView={archived_view}
                  />
                </>
              ) : archived_view ? (
                /* E5 — archived view, nothing archived. Needs a way back, or
                   the brand is stranded on an empty screen. */
                <EmptyState
                  titleAs="h3"
                  data-testid="brand-products-empty-no-archived"
                  title={t("settings.brand.products.empty.no_archived.title")}
                  description={t(
                    "settings.brand.products.empty.no_archived.description",
                  )}
                  actions={
                    <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
                      <Link href={brandSettingsProductsPath()}>
                        {t("settings.brand.products.empty.no_archived.cta")}
                      </Link>
                    </Button>
                  }
                />
              ) : store !== null && store.last_synced_at === null ? (
                /* E2 — connected, never synced. The SyncPanel's button above
                   is the action, so this state carries none of its own. */
                <EmptyState
                  titleAs="h3"
                  data-testid="brand-products-empty-never-synced"
                  title={t("settings.brand.products.empty.never_synced.title")}
                  description={t(
                    "settings.brand.products.empty.never_synced.description",
                  )}
                />
              ) : allArchived ? (
                /* E4 — synced, zero active, some archived. */
                <EmptyState
                  titleAs="h3"
                  data-testid="brand-products-empty-all-archived"
                  title={t("settings.brand.products.empty.all_archived.title")}
                  description={t(
                    "settings.brand.products.empty.all_archived.description",
                  )}
                  actions={
                    <Button asChild className="h-11 w-full sm:h-9 sm:w-auto">
                      <Link href={brandSettingsProductsPath({ archived: 1 })}>
                        {t("settings.brand.products.empty.all_archived.cta")}
                      </Link>
                    </Button>
                  }
                />
              ) : (
                /* E3 — synced and the store returned nothing. A real signal:
                   their products are probably unpublished. */
                <EmptyState
                  titleAs="h3"
                  data-testid="brand-products-empty-no-products"
                  title={t("settings.brand.products.empty.no_products.title")}
                  description={t(
                    "settings.brand.products.empty.no_products.description",
                    { at: syncedAt ?? "" },
                  )}
                />
              )}
            </>
          )}
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}

/**
 * Two Inertia `<Link>`s, not a form and not a `Select` (UX §2): the filter has
 * two values, and switching must be a plain navigation so the back button
 * works and the URL is shareable.
 *
 * Rendered whenever a store exists, INCLUDING when the current view is empty
 * — otherwise E5 has no way back.
 */
function FilterTabs({ archivedView }: { archivedView: boolean }) {
  const { t } = useTranslation()

  return (
    <div
      role="group"
      aria-label={t("settings.brand.products.filter_label")}
      className="flex gap-2"
      data-testid="brand-products-filter"
    >
      <FilterLink
        href={brandSettingsProductsPath()}
        active={!archivedView}
        label={t("settings.brand.products.filter_active")}
        testId="brand-products-filter-active"
      />
      <FilterLink
        href={brandSettingsProductsPath({ archived: 1 })}
        active={archivedView}
        label={t("settings.brand.products.filter_archived")}
        testId="brand-products-filter-archived"
      />
    </div>
  )
}

function FilterLink({
  href,
  active,
  label,
  testId,
}: {
  href: string
  active: boolean
  label: string
  testId: string
}) {
  return (
    <Button
      size="sm"
      variant={active ? "secondary" : "ghost"}
      asChild
      className={cn("h-11 sm:h-9")}
    >
      <Link
        href={href}
        data-testid={testId}
        aria-current={active ? "page" : undefined}
      >
        {label}
      </Link>
    </Button>
  )
}
